import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, Users, HandCoins, ShieldCheck, ChartLineUp } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import API from "@/lib/api";

const heroImage = "https://static.prod-images.emergentagent.com/jobs/42cd6bcf-2bc6-477f-ac22-e70b4de5ff36/images/ce4b9b05fd6fcb1ae03906c6c4c043452f4c84d65f61c5108a57906043ebcdc4.png";

const features = [
  { icon: Heart, title: "Bereavement Support", desc: "Whenever a member or their family faces a loss, the community rallies together through structured contributions." },
  { icon: Users, title: "Registered Community", desc: "Only approved members participate. Each member is accountable and visible within the Utheri family." },
  { icon: HandCoins, title: "M-Pesa Payments", desc: "Contribute directly from your phone via M-Pesa — fast, secure, and fully tracked." },
  { icon: ShieldCheck, title: "Full Transparency", desc: "Every contribution is recorded. Members can view statements and track all past events." },
];

const formatKES = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 0)}K`;
  return Number(n).toLocaleString();
};

export default function LandingPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    API.get("/public/milestones").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  const milestones = [
    { key: "members",        label: "Members",       icon: Users,        value: stats?.members,                       prefix: "" },
    { key: "totalCollected", label: "Total raised",  icon: HandCoins,    value: stats?.totalCollected, prefix: "KES ", isMoney: true },
    { key: "beneficiaries",  label: "Families helped", icon: Heart,      value: stats?.beneficiaries,                 prefix: "" },
    { key: "totalPayouts",   label: "Disbursed",     icon: ChartLineUp,  value: stats?.totalPayouts,    prefix: "KES ", isMoney: true },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navbar */}
      <header className="fixed top-0 inset-x-0 z-50 flex items-center justify-between px-6 py-4 bg-background/80 backdrop-blur-xl backdrop-saturate-150 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
            <Heart className="w-5 h-5 text-primary-foreground" weight="fill" />
          </div>
          <span className="font-heading text-xl font-bold text-foreground tracking-tighter">Utheri</span>
        </div>
        <Link to="/login" data-testid="header-sign-in-btn">
          <button className="px-5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold active:scale-95 transition-transform">
            Sign In
          </button>
        </Link>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img src={heroImage} alt="Community" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24">
          <motion.div className="max-w-xl" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <motion.span className="inline-block px-3 py-1 rounded-full text-xs font-bold tracking-[0.2em] uppercase bg-accent text-accent-foreground mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              Community Fund
            </motion.span>
            <motion.h1 className="font-heading text-5xl md:text-6xl font-semibold text-white leading-tight mb-6 tracking-tighter" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}>
              One community.<br />One heart.
            </motion.h1>
            <motion.p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}>
              Utheri is a registered community group where members support one another during times of bereavement — contributing KES 200 per event through M-Pesa.
            </motion.p>
            <motion.div className="flex gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              {/*
              <Link to="/register" data-testid="hero-register-btn">
                <button className="px-8 py-3 rounded-xl bg-accent text-accent-foreground text-base font-semibold active:scale-95 transition-transform">
                  Join Now
                </button>
              </Link>
              */}
              <Link to="/login" data-testid="hero-sign-in-btn">
                <button className="px-8 py-3 rounded-xl bg-white/15 backdrop-blur text-white text-base font-semibold border border-white/20 active:scale-95 transition-transform">
                  Sign In
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Milestones — community proof */}
      <section className="relative -mt-16 z-20 px-6 pb-4" data-testid="landing-milestones">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-border rounded-2xl shadow-xl px-6 py-8 sm:px-10 sm:py-10"
          >
            <div className="text-center mb-6">
              <span className="inline-block text-[10px] font-bold tracking-[0.2em] uppercase text-primary">Milestones</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tighter mt-1">What Utheri has accomplished together</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {milestones.map((m, i) => (
                <motion.div
                  key={m.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="text-center sm:text-left"
                  data-testid={`milestone-${m.key}`}
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 mb-3">
                    <m.icon className="w-5 h-5 text-primary" weight="duotone" />
                  </div>
                  <p className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-foreground" data-testid={`milestone-${m.key}-value`}>
                    {stats == null ? (
                      <span className="inline-block w-16 h-7 bg-muted rounded animate-pulse" />
                    ) : (
                      <>
                        {m.prefix}{m.isMoney ? formatKES(m.value) : Number(m.value || 0).toLocaleString()}
                      </>
                    )}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 font-medium">{m.label}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              Live numbers, updated every time a member contributes or a family receives support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-4xl font-semibold text-foreground mb-4 tracking-tighter">Built for the Utheri community</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A dedicated platform to manage contributions, track bereavements, and keep every member connected.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div key={f.title} className="bg-card rounded-xl p-6 border border-border shadow-sm" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="w-6 h-6 text-primary" weight="duotone" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-primary">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-4xl font-semibold text-primary-foreground mb-4 tracking-tighter">Join the Utheri family today</h2>
          <p className="text-primary-foreground/70 text-lg mb-8">Register your account, complete your profile, and become part of a community that cares.</p>
          {/*
          <Link to="/register" data-testid="cta-register-btn">
            <button className="px-8 py-3 rounded-xl bg-white text-primary text-base font-semibold active:scale-95 transition-transform">
              Get Started
            </button>
          </Link>
          */}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border bg-background text-sm text-muted-foreground">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>&copy; {new Date().getFullYear()} Utheri Community Fund. All rights reserved.</p>
          <Link to="/login" className="flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors" data-testid="footer-admin-link">
            <ShieldCheck className="w-3.5 h-3.5" />Admin Portal
          </Link>
        </div>
      </footer>
    </div>
  );
}
